import { Link } from '../components/Link.tsx';
import { LegalPage } from '../components/marketing/LegalPage.tsx';
import { useT } from '../i18n.ts';

export function Privacy() {
  const t = useT();

  return (
    <LegalPage
      title={t('privacy.title')}
      lede="What Goalpost collects, why, who else touches it, and how to get it changed or deleted."
      effectiveDate="September 18, 2026"
    >
      <h2 id="who">Who we are</h2>
      <p>
        Goalpost is operated by On A Hill LLC (“we”, “us”) at movegoalpost.com.
        Questions about this policy go to{' '}
        <a href="mailto:info@onahill.co">info@onahill.co</a>.
      </p>

      <h2 id="collect">What we collect</h2>
      <ul>
        <li>
          <strong>Account details.</strong> Your email address, a display name,
          and, where password sign-in is enabled, a hash of your password.
        </li>
        <li>
          <strong>Workspace content.</strong> The documents, specifications,
          changes, teams, and uploaded images you and your teammates put into a
          workspace. This is your content; we store and process it to run the
          service.
        </li>
        <li>
          <strong>Billing.</strong> Pro subscriptions are billed through Stripe,
          which holds your payment details. We keep a record of your subscription.
        </li>
        <li>
          <strong>Diagnostics.</strong> Error reports, performance data, and
          server logs, which include your IP address and browser details.
        </li>
        <li>
          <strong>Ad attribution.</strong> If you arrive from an ad, an
          identifier for that click may be kept so the ad can be credited when
          you sign up. Nothing else about you is shared with the ad network.
        </li>
      </ul>

      <h2 id="use">How we use it</h2>
      <p>
        To provide and secure the service, to send the email the service depends
        on (sign-in links, notifications, billing receipts), to bill Pro
        workspaces, to find and fix problems, to prevent abuse, and to measure
        whether our advertising works. We do not sell personal data and we do not
        use your workspace content to train machine-learning models.
      </p>

      <h2 id="ai">AI assistants and connected apps</h2>
      <p>
        You can connect an AI assistant or another app to Goalpost. When you do,
        the consent screen names the app, says where the grant is sent, and
        describes what it can do; the app then acts on your behalf inside the one
        workspace you chose. Goalpost does not itself send your content to any AI
        provider. What you and a connected assistant exchange is handled by that
        assistant’s provider under its own terms. You can see and revoke every
        connection from <strong>Profile → Connected apps</strong>; details are
        in <Link to="/docs/ai-integration">AI Integrations</Link>.
      </p>

      <h2 id="processors">Who else handles your data</h2>
      <p>
        We use service providers for hosting, file storage, payments, email
        delivery, error monitoring, bot protection, and ad measurement. Each
        processes data only to provide its service to us. The current list is
        available on request.
      </p>

      <h2 id="cookies">Cookies and browser storage</h2>
      <p>
        We use a cookie to keep you signed in, an ad-click identifier if you
        arrived from an ad, and browser storage for preferences such as your
        theme. We do not use third-party advertising or analytics cookies.
      </p>

      <h2 id="retention">How long we keep it</h2>
      <p>
        Account details and workspace content stay for as long as the account or
        workspace exists. Diagnostic data and backups are kept for a limited
        period and then expire.
      </p>

      <h2 id="rights">Your choices</h2>
      <p>
        Email <a href="mailto:info@onahill.co">info@onahill.co</a> to get a
        copy of your personal data, correct it, or delete your account. We will
        confirm your identity first and respond within 30 days. Content you
        contributed to a workspace owned by someone else stays with that
        workspace.
      </p>

      <h2 id="security">Security</h2>
      <p>
        Everything travels over TLS, passwords and access tokens are stored as
        hashes, and access to production systems is limited to the people who
        run the service. No system is perfectly secure; if we learn of a breach
        that affects you, we will tell you.
      </p>

      <h2 id="children">Children</h2>
      <p>Goalpost is not directed at children under 16, and we do not knowingly collect their data.</p>

      <h2 id="changes">Changes to this policy</h2>
      <p>
        When this policy changes we update the date at the top. For a change that
        materially affects how we handle your data, we will also email account
        holders.
      </p>
    </LegalPage>
  );
}
